import { useState, lazy, Suspense } from 'react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminConfigApi } from '../../services/api';
import { toast } from '../../stores';
import { NexusCard, NexusButton, NexusInput } from '../../components/nexus-ui';
import { Save, Play, Plus, X, Cpu, Shield, Sparkles, TestTube } from 'lucide-react';

const MonacoEditor = lazy(() => import('@monaco-editor/react'));

const TABS = [
  { id: 'prompt', label: 'System Prompt', icon: Sparkles },
  { id: 'model', label: 'Model Config', icon: Cpu },
  { id: 'guardrails', label: 'Guardrails', icon: Shield },
  { id: 'sandbox', label: 'Test Sandbox', icon: TestTube },
];

const MODELS = [
  { id: 'claude-opus-4-8', provider: 'cognara', label: 'Cognara Engine · Apex 4.8',  desc: 'Maximum reasoning, deepest analysis' },
  { id: 'claude-opus-4-7', provider: 'cognara', label: 'Cognara Engine · Ultra 4.7', desc: 'Premium reasoning, high accuracy' },
  { id: 'claude-opus-4-6', provider: 'cognara', label: 'Cognara Engine · Pro 4.6',   desc: 'Balanced reasoning (base model)' },
];

function TabBar({ activeTab, setActiveTab }) {
  return (
    <LayoutGroup>
      <div className="flex gap-1 p-1 bg-nexus-bg-tertiary/50 rounded-xl border border-white/5">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`relative flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id ? 'text-nexus-text-primary' : 'text-nexus-text-tertiary hover:text-nexus-text-secondary'
            }`}
          >
            {activeTab === tab.id && (
              <motion.div
                layoutId="tab-indicator"
                className="absolute inset-0 bg-nexus-bg-secondary rounded-lg border border-nexus-border-emphasized"
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              />
            )}
            <span className="relative z-10 flex items-center gap-2">
              <tab.icon size={16} />
              {tab.label}
            </span>
          </button>
        ))}
      </div>
    </LayoutGroup>
  );
}

function PromptTab({ config, onChange }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-nexus-text-secondary mb-2">Master System Prompt</label>
        <p className="text-xs text-nexus-text-tertiary mb-3">This prompt is prepended to every AI response across all tenants.</p>
        <Suspense fallback={<div className="h-[400px] bg-nexus-bg-tertiary rounded-lg animate-pulse" />}>
          <div className="rounded-lg overflow-hidden border border-nexus-border-emphasized">
            <MonacoEditor
              height="400px"
              language="markdown"
              theme="vs-dark"
              value={config?.master_system_prompt || ''}
              onChange={(v) => onChange('master_system_prompt', v)}
              options={{
                minimap: { enabled: false },
                fontSize: 14,
                wordWrap: 'on',
                lineNumbers: 'on',
                padding: { top: 16 },
                scrollBeyondLastLine: false,
              }}
            />
          </div>
        </Suspense>
      </div>
    </motion.div>
  );
}

function ModelTab({ config, onChange }) {
  const modelConfig = config?.master_model_config || {};
  const selectedModel = modelConfig.primary_model || 'claude-opus-4-6';
  const temperature = modelConfig.temperature ?? 0.7;
  const maxTokens = modelConfig.max_tokens ?? 4096;

  const tempColor = temperature < 0.3 ? '#2E70AD' : temperature < 0.7 ? '#1E4E8C' : temperature < 1.0 ? '#92580A' : '#7A1F1F';

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-nexus-text-secondary mb-3">Primary Model</label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {MODELS.map((m) => (
            <motion.button
              key={m.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onChange('master_model_config', { ...modelConfig, primary_model: m.id })}
              className={`relative p-4 rounded-xl border text-left transition-all ${
                selectedModel === m.id
                  ? 'border-nexus-accent-mint/50 bg-nexus-accent-mint/5 shadow-[0_0_20px_rgba(30, 78, 140,0.1)]'
                  : 'border-white/5 bg-nexus-bg-tertiary/50 hover:border-nexus-border-emphasized'
              }`}
            >
              {selectedModel === m.id && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 400 }}
                  className="absolute top-3 right-3 w-5 h-5 rounded-full bg-nexus-accent-mint flex items-center justify-center"
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6L5 9L10 3" stroke="#0A0E1A" strokeWidth="2" strokeLinecap="round" /></svg>
                </motion.div>
              )}
              <p className="text-sm font-semibold text-nexus-text-primary">{m.label}</p>
              <p className="text-xs text-nexus-text-tertiary mt-1">{m.desc}</p>
              <span className="inline-block mt-2 text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-nexus-text-tertiary">{m.provider}</span>
            </motion.button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-nexus-text-secondary mb-2">
            Temperature: <span style={{ color: tempColor }}>{temperature.toFixed(2)}</span>
          </label>
          <input
            type="range" min="0" max="2" step="0.05" value={temperature}
            onChange={(e) => onChange('master_model_config', { ...modelConfig, temperature: parseFloat(e.target.value) })}
            className="w-full h-2 rounded-full appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, #2E70AD, #1E4E8C 35%, #92580A 65%, #7A1F1F)`,
            }}
          />
          <div className="flex justify-between text-[10px] text-nexus-text-tertiary mt-1">
            <span>Precise</span><span>Creative</span><span>Random</span>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-nexus-text-secondary mb-2">Max Tokens: {maxTokens}</label>
          <input
            type="range" min="256" max="16384" step="256" value={maxTokens}
            onChange={(e) => onChange('master_model_config', { ...modelConfig, max_tokens: parseInt(e.target.value) })}
            className="w-full h-2 bg-nexus-bg-tertiary rounded-full appearance-none cursor-pointer"
          />
        </div>
      </div>
    </motion.div>
  );
}

function GuardrailsTab({ config, onChange }) {
  const guardrails = config?.master_guardrails || {};
  const [newTopic, setNewTopic] = useState('');
  const [newDisclaimer, setNewDisclaimer] = useState('');
  const blockedTopics = guardrails.blocked_topics || [];
  const disclaimers = guardrails.required_disclaimers || [];

  const addItem = (field, value, setter) => {
    if (!value.trim()) return;
    const current = guardrails[field] || [];
    onChange('master_guardrails', { ...guardrails, [field]: [...current, value.trim()] });
    setter('');
  };

  const removeItem = (field, index) => {
    const current = [...(guardrails[field] || [])];
    current.splice(index, 1);
    onChange('master_guardrails', { ...guardrails, [field]: current });
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-nexus-text-secondary mb-2">Blocked Topics</label>
        <div className="flex gap-2 mb-3">
          <NexusInput
            value={newTopic}
            onChange={(e) => setNewTopic(e.target.value)}
            placeholder="Add topic to block..."
            onKeyDown={(e) => e.key === 'Enter' && addItem('blocked_topics', newTopic, setNewTopic)}
            className="flex-1"
          />
          <NexusButton size="sm" onClick={() => addItem('blocked_topics', newTopic, setNewTopic)}>
            <Plus size={16} />
          </NexusButton>
        </div>
        <div className="flex flex-wrap gap-2">
          <AnimatePresence>
            {blockedTopics.map((topic, i) => (
              <motion.span
                key={topic}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 400 }}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-500/10 text-red-400 rounded-lg text-xs border border-red-500/20"
              >
                {topic}
                <button onClick={() => removeItem('blocked_topics', i)} className="hover:text-red-300">
                  <X size={12} />
                </button>
              </motion.span>
            ))}
          </AnimatePresence>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-nexus-text-secondary mb-2">Required Disclaimers</label>
        <div className="flex gap-2 mb-3">
          <NexusInput
            value={newDisclaimer}
            onChange={(e) => setNewDisclaimer(e.target.value)}
            placeholder="Add disclaimer..."
            onKeyDown={(e) => e.key === 'Enter' && addItem('required_disclaimers', newDisclaimer, setNewDisclaimer)}
            className="flex-1"
          />
          <NexusButton size="sm" onClick={() => addItem('required_disclaimers', newDisclaimer, setNewDisclaimer)}>
            <Plus size={16} />
          </NexusButton>
        </div>
        <div className="flex flex-wrap gap-2">
          <AnimatePresence>
            {disclaimers.map((d, i) => (
              <motion.span
                key={d}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 400 }}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-500/10 text-amber-400 rounded-lg text-xs border border-amber-500/20"
              >
                {d}
                <button onClick={() => removeItem('required_disclaimers', i)} className="hover:text-amber-300">
                  <X size={12} />
                </button>
              </motion.span>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
}

function SandboxTab() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sources, setSources] = useState([]);

  const sendTest = async () => {
    if (!input.trim() || loading) return;
    const userMsg = { role: 'user', content: input };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    setSources([]);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/orchestrate/chat/stream`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: userMsg.content,
            tenant_id: 'admin-sandbox',
            stream: true,
          }),
        }
      );

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';

      setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const text = decoder.decode(value);
        const lines = text.split('\n').filter(l => l.startsWith('data: '));

        for (const line of lines) {
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'content') {
              fullContent += data.text;
              const currentContent = fullContent;
              setMessages((prev) => {
                const msgs = [...prev];
                msgs[msgs.length - 1] = { role: 'assistant', content: currentContent };
                return msgs;
              });
            } else if (data.type === 'sources') {
              setSources(data.sources || []);
            }
          } catch {}
        }
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `Error: ${err.message || 'Failed to get response'}` },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-4">
      <div className="h-[350px] overflow-y-auto bg-nexus-bg-primary/50 rounded-xl border border-white/5 p-4 space-y-3">
        {messages.length === 0 && (
          <div className="text-center text-nexus-text-tertiary text-sm py-12">
            <TestTube size={32} className="mx-auto mb-2 opacity-50" />
            Send a message to test your GPT configuration
          </div>
        )}
        <AnimatePresence>
          {messages.map((msg, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[80%] px-4 py-2.5 rounded-xl text-sm ${
                msg.role === 'user'
                  ? 'bg-nexus-accent-mint/10 text-nexus-text-primary border border-nexus-accent-mint/20'
                  : 'bg-nexus-bg-tertiary text-nexus-text-secondary border border-white/5'
              }`}>
                {msg.content}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        {loading && (
          <div className="flex gap-1 px-4 py-2">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                className="w-2 h-2 rounded-full bg-nexus-accent-mint/50"
              />
            ))}
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <NexusInput
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Test your GPT config..."
          onKeyDown={(e) => e.key === 'Enter' && sendTest()}
          className="flex-1"
        />
        <NexusButton onClick={sendTest} disabled={loading || !input.trim()}>
          <Play size={16} /> Run
        </NexusButton>
      </div>
    </motion.div>
  );
}

const AdminGPTConfig = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('prompt');
  const [localConfig, setLocalConfig] = useState(null);

  const { data: config, isLoading } = useQuery({
    queryKey: ['adminConfig'],
    queryFn: () => adminConfigApi.getConfig().then((r) => r.data),
  });

  // Initialize local state from fetched config
  if (config && !localConfig) {
    setLocalConfig(config);
  }

  const saveMutation = useMutation({
    mutationFn: (data) => adminConfigApi.updateConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['adminConfig']);
      toast.success('Configuration saved');
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Save failed'),
  });

  const handleChange = (field, value) => {
    setLocalConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    if (!localConfig) return;
    saveMutation.mutate({
      master_system_prompt: localConfig.master_system_prompt,
      master_model_config: localConfig.master_model_config,
      master_guardrails: localConfig.master_guardrails,
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-16 bg-nexus-bg-tertiary/50 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-gpt-config">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-nexus-text-primary">Master GPT Configuration</h2>
          <p className="text-nexus-text-secondary text-sm mt-1">Configure the AI engine that powers all tenant instances</p>
        </div>
        <NexusButton onClick={handleSave} isLoading={saveMutation.isPending} data-testid="save-config-btn">
          <Save size={16} /> Save Changes
        </NexusButton>
      </div>

      <TabBar activeTab={activeTab} setActiveTab={setActiveTab} />

      <NexusCard variant="glass" hover={false}>
        <div className="p-6">
          <AnimatePresence mode="wait">
            {activeTab === 'prompt' && <PromptTab key="prompt" config={localConfig} onChange={handleChange} />}
            {activeTab === 'model' && <ModelTab key="model" config={localConfig} onChange={handleChange} />}
            {activeTab === 'guardrails' && <GuardrailsTab key="guardrails" config={localConfig} onChange={handleChange} />}
            {activeTab === 'sandbox' && <SandboxTab key="sandbox" />}
          </AnimatePresence>
        </div>
      </NexusCard>
    </div>
  );
};

export default AdminGPTConfig;
